import { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "@/lib/api";

/**
 * AchievementsTab — 8×4 glassmorphism grid of 32 achievement cells (one row
 * left over for spacing/title at the top). Hidden achievements show as "???"
 * until earned. Rank tiers F→S are colored per spec.
 *
 * Reads /api/achievements/me which returns: { exp, unlocked[], titles[],
 * current_title, catalog[], ranks{} }.
 */

const RANK_ORDER = ["F", "D", "C", "B", "A", "S"];

export default function AchievementsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/achievements/me");
        setData(data);
      } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    })();
  }, []);

  const grid = useMemo(() => {
    if (!data) return [];
    // Sort: F→S then by their order in catalog so the grid reads logically.
    const order = { F: 0, D: 1, C: 2, B: 3, A: 4, S: 5 };
    return [...data.catalog].sort((a, b) =>
      (order[a.rank] - order[b.rank]) || 0
    );
  }, [data]);

  if (err) return <div className="text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-3 text-xs">{err}</div>;
  if (!data) return <div className="text-slate-500 text-sm p-3"><i className="fa-solid fa-spinner fa-spin mr-2"/>Loading achievements…</div>;

  const totalUnlocked = data.unlocked.length;
  const totalVisible = data.catalog.length;
  const currentTitle = data.current_title;

  return (
    <div data-testid="achievements-tab" className="space-y-4 max-w-5xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-slate-900/80 to-slate-900/40 border border-slate-800 rounded-xl px-4 py-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Trophy Case</div>
          <div className="text-sm text-white font-bold mt-0.5">
            <span data-testid="ach-unlocked-count" className="text-amber-300 tabular-nums">{totalUnlocked}</span>
            <span className="text-slate-500"> / {totalVisible} unlocked</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Total EXP</div>
          <div data-testid="ach-exp" className="text-2xl font-black text-amber-300 tabular-nums">{data.exp}</div>
        </div>
        {currentTitle && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Current Title</div>
            <div data-testid="ach-current-title"
              className="text-base font-black uppercase tracking-wider mt-0.5"
              style={{ color: currentTitle.color }}>
              {currentTitle.name}
            </div>
          </div>
        )}
      </div>

      {/* Rank legend */}
      <div className="flex flex-wrap gap-2 text-[10px] font-mono">
        {RANK_ORDER.map(letter => {
          const r = data.ranks[letter];
          return (
            <span key={letter} className="px-2 py-0.5 rounded border font-bold tracking-wider"
              style={{ color: r.color, borderColor: `${r.color}55`, background: `${r.color}14` }}
              data-testid={`ach-rank-key-${letter}`}>
              {letter} · {r.name} · {r.exp} EXP
            </span>
          );
        })}
      </div>

      {/* 8 col × 4 row grid */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2.5" data-testid="ach-grid">
        {grid.map(a => {
          const rank = data.ranks[a.rank];
          const unlocked = a.unlocked;
          const isHiddenLocked = a.hidden && !unlocked;
          return (
            <div
              key={a.id}
              data-testid={`ach-cell-${a.id}`}
              title={`${a.name} — ${a.desc}`}
              className={`relative group rounded-xl p-2.5 backdrop-blur transition-all hover:scale-[1.05] cursor-help select-none
                ${unlocked ? "border" : "border border-slate-800/80"}`}
              style={unlocked ? {
                borderColor: `${rank.color}aa`,
                background: `linear-gradient(135deg, ${rank.color}26, ${rank.color}08 70%, rgba(15,19,27,0.6))`,
                boxShadow: `0 0 0 1px ${rank.color}55, 0 0 14px ${rank.color}33`,
              } : {
                background: "linear-gradient(135deg, rgba(20,25,35,0.5), rgba(15,19,27,0.7))",
              }}>
              <div className="flex items-center justify-between">
                <span
                  className="text-[10px] font-black tracking-wider"
                  style={{ color: unlocked ? rank.color : "#475569" }}>
                  {a.rank}
                </span>
                <i className={`fa-solid ${unlocked ? "fa-check text-emerald-300" : isHiddenLocked ? "fa-question text-slate-600" : "fa-lock text-slate-600"} text-[10px]`}/>
              </div>
              <div className={`text-[11px] font-bold mt-1 line-clamp-2 ${unlocked ? "text-white" : "text-slate-500"}`}>
                {a.name}
              </div>
              <div className={`text-[9px] mt-1 line-clamp-3 ${unlocked ? "text-slate-300" : "text-slate-600"}`}>
                {a.desc}
              </div>
              <div className="text-[9px] mt-1.5 text-amber-300 font-bold tabular-nums opacity-80">
                +{rank.exp} EXP
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-slate-600 italic">
        Hover any cell for the full description. Hidden achievements stay masked until you discover the trigger.
      </div>
    </div>
  );
}
